import { css } from '@emotion/core'
import { usePageView } from '@fs/zion-analytics'
import {
  Autosuggest,
  Button,
  Cell,
  DataBlock,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Divider,
  FlowGrid,
  Grid,
  ResponsiveTableRow,
  Row,
  TextField,
  useOverlay,
  H5,
  DangerButton,
} from '@fs/zion-ui'
import React, { useContext, useEffect, useLayoutEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import { usePageTitle } from '../../app/PageTitleContext'
import { CisLink } from '../../app/Links'
import { RawData } from '../../common/RawData'
import { PersonContext } from '../PersonContext'
import { usePersonLabelsController } from './usePersonLabelsController'
import { PermissionsContext } from '../../app/PermissionsContext'

export default function PersonLabels() {
  usePageView({ page_detail: 'DMConsole:PersonLabels' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.person.labels'))
  const { currentPerson } = useContext(PersonContext)
  const { personLabelsState, personLabelsActions } = usePersonLabelsController()
  const { isAuthorized } = useContext(PermissionsContext)
  useEffect(() => {
    personLabelsActions.getPersonLabels()
    personLabelsActions.getPersonLabelMetadata()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPerson?.id])

  if (!(personLabelsState.adminLabels && personLabelsState.labelsSuggestions)) {
    return null
  }

  const nowrapCss = css`
    word-wrap: break-word;
  `

  function makeLabelText(label) {
    let labelText = label.humanReadableText || label.valueText
    if (label.oar || label.hco || label.locked || label.hidden || label.ownerRestricted) {
      labelText += ':'
      labelText += label.oar ? ` ${t('accessControl.oar', 'OAR')}` : ''
      labelText += label.hco ? ` ${t('accessControl.hco', 'HCO')}` : ''
      labelText += label.locked ? ` ${t('accessControl.locked', 'Locked')}` : ''
      labelText += label.hidden ? ` ${t('hidden', 'Hidden')}` : ''
      labelText += label.ownerRestricted ? ` ${t('ownerRestricted', 'Owner Restricted')}` : ''
    }
    return labelText
  }
  const toLinkOrNotToLink = (row) => {
    const name = row.contributorName || row.proxyName || ''
    const id = row.cisId || row.proxyId || ''
    if (!id) {
      return null
    }

    if (id.includes('cis.proc') || id.includes('cis.wkca')) {
      return (
        <span css={nowrapCss}>
          {name ? `${name} ` : ''}
          {id ? `(${id})` : ''}
        </span>
      )
    }

    return <CisLink userId={id.split('.')[2]} userName={name} />
  }

  const isDisabled = (label) => {
    if (!label) {
      return true
    }

    // If the label has an updatePermission, check if the user is authorized for it
    // If it does not have an updatePermission, check if the label is in the suggestions
    // and if it has an updatePermission, check that too
    if (label.updatePermission) {
      return !isAuthorized(label.updatePermission)
    }

    const found = personLabelsState.labelsSuggestions.find(
      (suggestion) => suggestion.label?.valueText === label.valueText
    )
    return !found || (found.label?.updatePermission && !isAuthorized(found.label.updatePermission))
  }

  return (
    <Grid>
      {/* Row */}
      <Cell columns={12}>
        <H5>{t('labels.labels', 'Labels')}</H5>
        <DmcResponsiveTable
          id="labelsTable"
          totalColumnCount={5}
          atSizeColumns={{
            default: {
              labels: personLabelsState.constants.labelTableHeaders.map((rowHeader) => {
                return rowHeader.label
              }),
            },
          }}
        >
          {personLabelsState.adminLabels.labels &&
            personLabelsState.adminLabels.labels.map((row) => {
              return (
                <ResponsiveTableRow
                  key={row.id}
                  column1={makeLabelText(row)}
                  column2={row.genealogicalDate}
                  column3={toLinkOrNotToLink(row)}
                  column4={row.reason}
                  column5={
                    <Row wrap>
                      <EditLabel
                        disabled={isDisabled(row)}
                        noShrinkX
                        labelData={row}
                        personLabelsState={personLabelsState}
                        personLabelsActions={personLabelsActions}
                      />
                      <DeleteLabel
                        disabled={isDisabled(row)}
                        noShrinkX
                        labelData={row}
                        personLabelsState={personLabelsState}
                        personLabelsActions={personLabelsActions}
                      />
                    </Row>
                  }
                />
              )
            })}
        </DmcResponsiveTable>
      </Cell>
      {/* Row */}
      <Cell columns={12}>
        <AddLabel personLabelsState={personLabelsState} personLabelsActions={personLabelsActions} />
      </Cell>
      {/* Row */}
      <Cell columns={12}>
        <H5>{t('labels.changeHistory', 'Change History')}</H5>
        <DmcResponsiveTable
          id="changeHistoryTable"
          totalColumnCount={5}
          atSizeColumns={{
            default: {
              labels: personLabelsState.constants.changeTableHeaders.map((row) => {
                return row.label
              }),
            },
          }}
        >
          {personLabelsState.adminLabels.changes &&
            personLabelsState.adminLabels.changes.map((row) => {
              return (
                <ResponsiveTableRow
                  key={row.id}
                  column1={row.changeAction}
                  column2={row.humanReadableText || row.valueText}
                  column3={toLinkOrNotToLink(row)}
                  column4={row.genealogicalDate}
                  column5={row.reason}
                />
              )
            })}
        </DmcResponsiveTable>
      </Cell>
      {/* Row */}
      <Cell columns={6}>
        <RawData state={personLabelsState} />
      </Cell>
      <Cell columns={6}>
        <RawData state={currentPerson} />
      </Cell>
    </Grid>
  )
}

function AddLabel({ personLabelsState, personLabelsActions }) {
  const textFieldRef = useRef()
  const { t } = useTranslation()

  useLayoutEffect(() => {
    textFieldRef?.current?.click()
    textFieldRef?.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textFieldRef?.current, personLabelsState.adding])

  return (
    <>
      {!personLabelsState.adding && (
        <Button
          id="addLabelBtn"
          emphasis="medium"
          title="open add labels"
          onClick={() => {
            personLabelsActions.setAdding(!personLabelsState.adding)
          }}
        >
          {t('action.add', 'Add')}
        </Button>
      )}
      {personLabelsState.adding && (
        <Grid>
          <Cell columns={12}>
            <Divider />
          </Cell>
          <Cell columns={12}>
            <Autosuggest
              id="addAdminLabelLbl"
              name="addAdminLabelLbl"
              label={t('label', 'Label')}
              selectedItem={personLabelsState.labelSuggestion}
              suggestions={personLabelsState.labelsSuggestions}
              onSuggestionSelected={(newSuggestion) =>
                newSuggestion && personLabelsActions.setLabelSuggestion(newSuggestion)
              }
              onClear={() => personLabelsActions.setLabelSuggestion(undefined)}
              placeholder={t('none', 'None')}
              highlightMatchingText
              clearable
              ref={textFieldRef}
            />
          </Cell>
          {personLabelsState.labelSuggestion && (
            <Cell columns={12}>
              <DataRows object={personLabelsState.labelSuggestion.label} />
            </Cell>
          )}
          <Cell columns={12}>
            <TextField
              label={t('reason', 'Reason')}
              key="addAdminAddReasonKey"
              id="addAdminAddReason"
              name="addAdminAddReasonKey"
              value={personLabelsState.addReason}
              onChange={(event) => personLabelsActions.setAddReason(event.target.value)}
            />
          </Cell>
          <Cell columns={12}>
            <Row>
              <Button
                id="confirmAddBtn"
                emphasis="high"
                onClick={() => {
                  personLabelsActions.addLabel()
                }}
              >
                {t('action.add', 'Add')}
              </Button>
              <Button emphasis="medium" onClick={personLabelsActions.reset}>
                {t('action.cancel', 'Cancel')}
              </Button>
            </Row>
          </Cell>
        </Grid>
      )}
    </>
  )
}

function EditLabel({ labelData, personLabelsState, personLabelsActions, disabled }) {
  const overlay = useOverlay()
  const { t } = useTranslation()
  const focusElementRef = useRef()
  return (
    <>
      <Button
        disabled={disabled}
        emphasis="medium"
        title="edit label"
        onClick={() => {
          personLabelsActions.setLabelReason(labelData.reason)
          overlay.open()
        }}
      >
        {t('action.edit', 'Edit')}
      </Button>
      <DialogOverlay
        {...overlay}
        focusRef={focusElementRef}
        title={t('person.labels.editLabelReason', 'Edit Label Reason')}
      >
        <Divider />
        <i>{labelData.humanReadableText || labelData.valueText}</i>
        <TextField
          ref={focusElementRef}
          label={t('reason', 'Reason')}
          key="updateLabelReason"
          id="updateLabelReason"
          name="updateLabelReasonKey"
          value={personLabelsState.labelReason}
          onChange={(event) => personLabelsActions.setLabelReason(event.target.value)}
        />
        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button
              id="confirmEditBtn"
              onClick={() => {
                overlay.close()
                personLabelsActions.editLabel(labelData)
              }}
              type="submit"
              emphasis="high"
            >
              {t('action.confirm', 'Confirm')}
            </Button>
            <Button onClick={overlay.close} emphasis="medium">
              {t('action.cancel', 'Cancel')}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      </DialogOverlay>
    </>
  )
}

function DeleteLabel({ labelData, personLabelsState, personLabelsActions, disabled }) {
  const overlay = useOverlay()
  const { t } = useTranslation()
  return (
    <>
      <DangerButton disabled={disabled} title="delete labels" onClick={overlay.open}>
        {t('action.delete', 'Delete')}
      </DangerButton>
      <DialogOverlay {...overlay} title={t('person.labels.deleteLabel', 'Delete Label')}>
        {t('person.labels.delete.confirm', 'Are you sure you want to delete this label?')}
        <br />
        <br />
        <Divider />
        <br />
        <i>{labelData.humanReadableText || labelData.valueText}</i>
        <TextField
          label={t('reason', 'Reason')}
          key="deleteAdminAddReasonKey"
          id="deleteAdminAddReason"
          name="deleteAdminAddReasonKey"
          value={personLabelsState.deleteReason}
          onChange={(event) => personLabelsActions.setDeleteReason(event.target.value)}
        />
        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button
              id="confirmDeleteBtn"
              onClick={() => personLabelsActions.deleteLabel(labelData)}
              type="submit"
              emphasis="high"
            >
              {t('action.delete', 'Delete')}
            </Button>
            <Button onClick={overlay.close} emphasis="medium">
              {t('action.cancel', 'Cancel')}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      </DialogOverlay>
    </>
  )
}

function DataRows({ object }) {
  if (!object || Object.keys(object).length === 0) {
    return null
  }
  return (
    <Grid>
      <p columns={2} />
      <Cell columns={10}>
        <FlowGrid columnCount={2} orientation="vertical">
          {Object.keys(object).map((key) => {
            // eslint-disable-next-line no-nested-ternary
            const value = object[key] === true ? 'Yes' : object[key] === false ? 'No' : object[key]
            return <DataBlock inline heading={`${key}:`} data={value} />
          })}
        </FlowGrid>
      </Cell>
    </Grid>
  )
}
